import React, { useState } from 'react';
import axios from 'axios';
import GoogleSignIn from './components/GoogleSignIn';
import './App.css';

// API base URL: the API origin only — do not include a path or trailing
// slash. The source code appends `/api/...` for each endpoint, so the
// final URL is `${API_BASE_URL}/api/upload` etc. For local dev leave this
// empty: the CRA proxy in client/package.json routes the resulting
// relative `/api/*` calls to http://localhost:5000. In production set
// this to the Render origin (e.g. `https://eyd-jasa.onrender.com`) in
// the Cloudflare Pages env-var settings.
const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

function App() {
  const [file, setFile] = useState(null);
  const [originalText, setOriginalText] = useState('');
  const [correctedText, setCorrectedText] = useState('');
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(''); // human-readable progress for long docs
  const [googleDocsUrl, setGoogleDocsUrl] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [googleAccessToken, setGoogleAccessToken] = useState('');

  // Split text on paragraph boundaries. Each chunk stays under ~30s of
  // MiniMax processing time so the 180s axios timeout covers cold start + 3 chunks.
  const chunkText = (text, maxChunkSize = 1500) => {
    const paragraphs = text.split(/\n\s*\n/);
    const chunks = [];
    let current = '';
    for (const p of paragraphs) {
      if (current.length + p.length + 2 > maxChunkSize && current) {
        chunks.push(current.trim());
        current = p;
      } else {
        current = current ? current + '\n\n' + p : p;
      }
    }
    if (current.trim()) chunks.push(current.trim());
    // Sub-split any oversized chunk by sentences
    const out = [];
    for (const c of chunks) {
      if (c.length <= maxChunkSize) { out.push(c); continue; }
      const sentences = c.split(/(?<=[.!?])\s+/);
      let sub = '';
      for (const s of sentences) {
        if (sub.length + s.length > maxChunkSize && sub) {
          out.push(sub.trim()); sub = s;
        } else {
          sub = sub ? sub + ' ' + s : s;
        }
      }
      if (sub.trim()) out.push(sub.trim());
    }
    return out;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileName(selectedFile ? selectedFile.name : '');
    setError('');
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    console.log('Starting file upload for:', file.name, file.type, file.size); // Log file info

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    setError('');

    try {
      console.log('Sending upload request...'); // Log request start
      const response = await axios.post(`${API_BASE_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Upload response received:', response.status, response.data); // Log response

      if (response.data.success) {
        console.log('File processed successfully, text length:', response.data.originalText?.length); // Log success
        setOriginalText(response.data.originalText);
        setCorrectedText('');
      } else {
        console.error('Server error processing file:', response.data.error); // Log server error
        setError(response.data.error || 'Error processing file');
      }
    } catch (err) {
      console.error('Upload error:', err.message); // Log error
      console.error('Error response:', err.response); // Log error response
      setError(err.response?.data?.error || 'Error uploading file');
    } finally {
      setLoading(false);
    }
  };

  const handleCorrect = async () => {
    if (!originalText) {
      setError('No text to correct');
      return;
    }

    setLoading(true);
    setError('');
    setProgress('');

    console.log('Sending correction request with text length:', originalText.length);

    // Split into small chunks so each request finishes well under the
    // 180s axios timeout. AI processes one chunk per call.
    const chunks = chunkText(originalText);
    console.log(`Split into ${chunks.length} chunks for client-side processing`);

    if (chunks.length === 0) {
      setError('No text to correct');
      setLoading(false);
      return;
    }

    // For short docs (1 chunk), keep the original single-request path.
    if (chunks.length === 1) {
      try {
        const response = await axios.post(`${API_BASE_URL}/api/correct`, { text: chunks[0] }, { timeout: 180000 });
        if (response.data.success && response.data.correctedText) {
          setCorrectedText(response.data.correctedText);
          if (response.data.processingTime) {
            console.log('Server processing time:', response.data.processingTime, 'ms');
          }
        } else {
          setError(response.data.error || 'Error correcting text');
        }
      } catch (err) {
        console.error('Correction error:', err);
        setError(err.response?.data?.error || 'Error correcting text');
      } finally {
        setLoading(false);
        setProgress('');
      }
      return;
    }

    // Multi-chunk: sequential requests, aggregate results, show progress.
    const corrected = [];
    try {
      for (let i = 0; i < chunks.length; i++) {
        setProgress(`Memproses bagian ${i + 1} dari ${chunks.length}…`);
        console.log(`Sending chunk ${i + 1}/${chunks.length} (${chunks[i].length} chars)`);

        const response = await axios.post(
          `${API_BASE_URL}/api/correct`,
          { text: chunks[i] },
          { timeout: 180000 }
        );

        if (response.data.success && response.data.correctedText) {
          corrected.push(response.data.correctedText);
        } else {
          throw new Error(response.data.error || `Chunk ${i + 1} returned no text`);
        }
      }
      setCorrectedText(corrected.join('\n\n'));
    } catch (err) {
      console.error('Correction error:', err);
      setError(err.response?.data?.error || err.message || 'Error correcting text');
      // If we have partial results, show them so user doesn't lose work.
      if (corrected.length > 0) {
        setCorrectedText(corrected.join('\n\n') + '\n\n[-- koreksi dihentikan di sini karena error --]');
      }
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const handleDownload = () => {
    if (!correctedText) return;

    const blob = new Blob([correctedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.replace(/\.[^/.]+$/, '') + '_corrected.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Handle Google Sign-In success
  const handleGoogleSignInSuccess = (authData) => {
    console.log('Google Sign-In successful:', authData);
    setIsAuthenticated(true);
    setUser(authData.user);
    setGoogleAccessToken(authData.token);
    setError('');
  };

  // Handle Google Sign-In error
  const handleGoogleSignInError = (error) => {
    console.error('Google Sign-In error:', error);
    setError('Google Sign-In failed: ' + error);
  };

  const handleExportToGoogleDocs = async () => {
    if (!correctedText) {
      setError('No corrected text to export');
      return;
    }

    if (!isAuthenticated || !googleAccessToken) {
      setError('Please sign in with Google first');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      console.log('Exporting to Google Docs with text length:', correctedText.length);
      const response = await axios.post(`${API_BASE_URL}/api/export/google-docs`, {
        text: correctedText,
        title: fileName.replace(/\.[^/.]+$/, '') + ' - Corrected',
        accessToken: googleAccessToken
      });

      if (response.data.success) {
        setGoogleDocsUrl(response.data.documentUrl);
        // Open the Google Doc in a new tab
        window.open(response.data.documentUrl, '_blank');
        console.log('Successfully exported to Google Docs:', response.data.documentUrl);
      } else {
        setError(response.data.error || 'Error exporting to Google Docs');
        console.error('Error exporting to Google Docs:', response.data.error);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error exporting to Google Docs');
      console.error('Error exporting to Google Docs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>📚 Indonesian Scientific Paper Corrector</h1>
        <p>Upload your Indonesian scientific paper in DOCX or PDF format for grammar and spelling correction.</p>
        <p>Powered by MiniMax-M3.</p>
      </header>

      <main className="App-main">
        {/* Authentication Section */}
        {!isAuthenticated && (
          <div className="auth-section">
            <h2>Sign in with Google</h2>
            <p>Please sign in with your Google account to export documents to Google Docs</p>
            <GoogleSignIn 
              onSuccess={handleGoogleSignInSuccess}
              onError={handleGoogleSignInError}
            />
          </div>
        )}

        {isAuthenticated && user && (
          <div className="user-info">
            <p>Signed in as: {user.name} ({user.email})</p>
          </div>
        )}

        <div className="upload-section">
          <div className="file-input-container">
            <input
              type="file"
              id="file-upload"
              accept=".docx,.pdf"
              onChange={handleFileChange}
              disabled={loading || !isAuthenticated}
            />
            <label htmlFor="file-upload" className="file-label">
              {fileName || 'Choose a DOCX or PDF file'}
            </label>
            <button 
              onClick={handleUpload} 
              disabled={loading || !file || !isAuthenticated}
              className="upload-button"
            >
              {loading ? 'Processing...' : 'Upload and Extract Text'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}
        </div>

        {originalText && (
          <div className="text-section">
            <h2>Original Text</h2>
            <textarea
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              rows={10}
              className="text-area"
            />

            <button 
              onClick={handleCorrect} 
              disabled={loading}
              className="correct-button"
            >
              {loading ? (
                <div className="button-content">
                  <div className="loading-spinner"></div>
                  {progress || 'Memperbaiki teks dengan MiniMax...'}
                </div>
              ) : (
                'Perbaiki Teks dengan MiniMax'
              )}
            </button>
          </div>
        )}

        {correctedText && (
          <div className="text-section">
            <h2>Corrected Text</h2>
            <textarea
              value={correctedText}
              readOnly
              rows={10}
              className="text-area corrected"
            />

            <div className="button-group">
              <button 
                onClick={handleDownload}
                disabled={!correctedText || loading}
                className="download-button"
              >
                {loading ? (
                  <div className="button-content">
                    <div className="loading-spinner"></div>
                    Generating file...
                  </div>
                ) : (
                  'Download as TXT'
                )}
              </button>
              
              <button 
                onClick={handleExportToGoogleDocs}
                disabled={loading || !isAuthenticated}
                className="export-google-docs-button"
              >
                {loading ? (
                  <div className="button-content">
                    <div className="loading-spinner"></div>
                    Exporting to Google Docs...
                  </div>
                ) : (
                  'Export to Google Docs'
                )}
              </button>
            </div>
            
            {googleDocsUrl && (
              <div className="success-message">
                <p>Successfully exported to Google Docs!</p>
                <a href={googleDocsUrl} target="_blank" rel="noopener noreferrer">
                  Open in Google Docs
                </a>
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="App-footer">
        <p>Indonesian Scientific Paper Corrector &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

export default App;